import React, { useEffect, useState, useRef } from "react";
import JoditEditor from "jodit-react";
import Topbar from "../../components/Topbar";
import Sidebar from "../../components/Sidebar";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";

// Helper function to format image URLs for display
const getImageUrl = (imagePath) => {
  if (!imagePath || typeof imagePath !== "string") return "";
  // If the path is already a full URL, return it directly
  if (imagePath.startsWith("http")) {
    return imagePath;
  }
  // Otherwise, construct the URL from the base URL and the relative path
  const baseUrl = process.env.REACT_APP_APIURL;
  const cleanPath = imagePath.replace(/\\/g, "/").replace(/^\/+/, "");
  return `${baseUrl}/${cleanPath}`;
};

// Helper function to format time (e.g., "14:00" -> "2:00 PM")
const formatTime = (timeString) => {
  if (!timeString) return "N/A";
  try {
    const [hours, minutes] = timeString.split(":");
    const h = parseInt(hours);
    const ampm = h >= 12 ? "PM" : "AM";
    const formattedHours = h % 12 || 12;
    return `${formattedHours}:${minutes} ${ampm}`;
  } catch (e) {
    return timeString;
  }
};

function UpdateProduct() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editor = useRef(null);
  const token = sessionStorage.getItem("token");
  const baseUrl = process.env.REACT_APP_APIURL;

  const [loading, setLoading] = useState(false);
  const [formdata, setFormdata] = useState({
    product_name: "",
    category: "",
    sub_category: "",
    sku: "",
    variation: "Simple",
    description: "",
    reguler_price: "",
    discount_price: "",
    stock: "",
    attributes: [],
    attributeValues: {},
    combinedValue: [],
    priceData: {},
    discountPriceData: {},
    stockData: {},
    displayShifts: [],
  });
  const [shiftList, setShiftList] = useState([]);

  const [categoryList, setCategoryList] = useState([]);
  const [subCategoryList, setSubCategoryList] = useState([]);
  const [attributeName, setAttributeName] = useState("");
  const [tempValues, setTempValues] = useState({});

  // State for image previews and new file uploads
  const [imagePreview, setImagePreview] = useState(null);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const [newProductImage, setNewProductImage] = useState(null);
  const [newGalleryImages, setNewGalleryImages] = useState([]);

  // Fetch initial product data and categories
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        // Fetch categories
        const catResponse = await axios.get(`${baseUrl}api/catagorylist?isAdmin=true`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCategoryList(catResponse.data?.catagories || []);

        // Fetch product details
        const prodResponse = await axios.get(
          `${baseUrl}api/productdetails/${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        // Fetch shifts
        const shiftResponse = await axios.get(`${baseUrl}api/shift/all`);
        setShiftList(shiftResponse.data?.data || []);

        if (prodResponse.data.success) {
          const product = prodResponse.data.product;

          // **FIXED**: Fetch sub-categories using the direct category ID from the product
          if (product.category) {
            const subCatResponse = await axios.get(
              `${baseUrl}api/sub-category/list?category_id=${product.category}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            setSubCategoryList(subCatResponse.data?.data || []);
          }

          // Set image previews for existing images
          if (product.productImage) {
            setImagePreview(getImageUrl(product.productImage));
          }
          if (product.galleryImages && product.galleryImages.length > 0) {
            setGalleryPreviews(product.galleryImages.map(getImageUrl));
          }

          // This logic populates the state for variable products
          let initialAttributes = [];
          let initialAttributeValues = {};
          let initialCombinedValues = [];
          let initialPriceData = {};
          let initialDiscountPriceData = {};
          let initialStockData = {};

          if (
            product.variation === "Variable" &&
            product.attributes?.length > 0
          ) {
            const rawAttributes = product.attributes[0];
            const attributeKeys = Object.keys(rawAttributes).filter(
              (k) => k !== "other_attributes" && k !== "_id"
            );
            initialAttributes = attributeKeys;

            const tempAttrValues = {};
            attributeKeys.forEach((key) => (tempAttrValues[key] = new Set()));

            product.attributes.forEach((attrInstance) => {
              const comboParts = [];
              attributeKeys.forEach((key) => {
                tempAttrValues[key].add(attrInstance[key]);
                comboParts.push(attrInstance[key]);
              });
              const comboString = comboParts.join(" - ");
              initialCombinedValues.push(comboString);

              if (attrInstance.other_attributes) {
                initialPriceData[comboString] =
                  attrInstance.other_attributes.price;
                initialDiscountPriceData[comboString] =
                  attrInstance.other_attributes.discount_price;
                initialStockData[comboString] =
                  attrInstance.other_attributes.stock;
              }
            });

            Object.keys(tempAttrValues).forEach((key) => {
              initialAttributeValues[key] = Array.from(tempAttrValues[key]);
            });
          }

          // **FIXED**: Set form data using the direct IDs for category and sub_category
          setFormdata({
            product_name: product.product_name || "",
            category: product.category || "",
            sub_category: product.sub_category || "",
            sku: product.sku || "",
            variation: product.variation || "Simple",
            description: product.description || "",
            reguler_price: product.reguler_price || "",
            discount_price: product.discount_price || "",
            stock: product.stock || "",
            // Variable product data
            attributes: initialAttributes,
            attributeValues: initialAttributeValues,
            combinedValue: initialCombinedValues,
            priceData: initialPriceData,
            discountPriceData: initialDiscountPriceData,
            stockData: initialStockData,
            displayShifts: product.displayShifts || [],
          });
        }
      } catch (error) {
        toast.error(
          "Failed to fetch initial data. " +
          (error.response?.data?.message || "")
        );
      } finally {
        setLoading(false);
      }
    };

    if (id && token) {
      fetchInitialData();
    }
  }, [id, token, baseUrl]);

  // Handler functions
  const handleVariationChange = (e) => {
    setFormdata({ ...formdata, variation: e.target.value });
  };

  const handleAttributeNameChange = (e) => {
    setAttributeName(e.target.value);
  };

  const handleAddAttribute = () => {
    if (attributeName.trim() !== "") {
      setFormdata({
        ...formdata,
        attributes: [...formdata.attributes, attributeName],
      });
      setAttributeName("");
    }
  };

  const handleAttributeValueChange = (attribute, e) => {
    setTempValues({
      ...tempValues,
      [attribute]: e.target.value,
    });
  };

  const handleAddValue = (attribute) => {
    const value = tempValues[attribute];
    if (value && value.trim() !== "") {
      setFormdata((prev) => {
        const updatedValues = {
          ...prev,
          attributeValues: {
            ...prev.attributeValues,
            [attribute]: [...(prev.attributeValues[attribute] || []), value],
          },
        };
        setTempValues({ ...tempValues, [attribute]: "" });
        return updatedValues;
      });
    }
  };


  useEffect(() => {
    if (formdata.variation === "Variable") {
      const generateCombinedValue = () => {
        const attributesArray = Object.values(formdata.attributeValues).filter(
          (arr) => arr.length > 0
        );
        if (attributesArray.length === 0) return [];

        const combine = (arr) =>
          arr.reduce(
            (acc, curr) => acc.flatMap((c) => curr.map((v) => [...c, v])),
            [[]]
          );

        return combine(attributesArray).map((combo) => combo.join(" - "));
      };

      const newCombinedValues = generateCombinedValue();
      setFormdata((prev) => ({
        ...prev,
        combinedValue: newCombinedValues,
      }));
    }
  }, [formdata.attributeValues, formdata.variation]);

  const handlePriceChange = (combo, e) => {
    setFormdata((prev) => ({
      ...prev,
      priceData: { ...prev.priceData, [combo]: e.target.value },
    }));
  };

  const handleDiscountPriceChange = (combo, e) => {
    setFormdata((prev) => ({
      ...prev,
      discountPriceData: { ...prev.discountPriceData, [combo]: e.target.value },
    }));
  };

  const handleStockChange = (combo, e) => {
    setFormdata((prev) => ({
      ...prev,
      stockData: { ...prev.stockData, [combo]: e.target.value },
    }));
  };

  const handleProductImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewProductImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleGalleryImagesChange = (e) => {
    const files = Array.from(e.target.files);
    setNewGalleryImages(files);
    const previewUrls = files.map((file) => URL.createObjectURL(file));
    // When new gallery images are selected, show only their previews.
    setGalleryPreviews(previewUrls);
  };

  const handleChangeCategory = async (e) => {
    const categoryId = e.target.value;
    setFormdata({ ...formdata, category: categoryId, sub_category: "" }); // Reset sub-category
    if (categoryId) {
      try {
        const response = await axios.get(
          `${baseUrl}api/sub-category/list?category_id=${categoryId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSubCategoryList(response.data?.data || []);
      } catch (error) {
        toast.error("Error fetching sub-categories.");
        setSubCategoryList([]);
      }
    } else {
      setSubCategoryList([]);
    }
  };

  const validateForm = () => {
    if (!formdata.product_name.trim()) {
      toast.error("Product Name is required.");
      return false;
    }
    if (!formdata.category) {
      toast.error("Category is required.");
      return false;
    }
    if (!formdata.sub_category) {
      toast.error("Sub Category is required.");
      return false;
    }
    if (!formdata.sku.trim()) {
      toast.error("SKU is required.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    const submitFormData = new FormData();

    // Append text fields
    submitFormData.append("product_name", formdata.product_name);
    submitFormData.append("category", formdata.category);
    submitFormData.append("sub_category", formdata.sub_category);
    submitFormData.append("sku", formdata.sku);
    submitFormData.append("variation", formdata.variation);
    submitFormData.append("description", formdata.description);
    submitFormData.append("displayShifts", JSON.stringify(formdata.displayShifts));

    if (formdata.variation === "Simple") {
      submitFormData.append("reguler_price", Number(formdata.reguler_price));
      submitFormData.append("discount_price", Number(formdata.discount_price));
      submitFormData.append("stock", Number(formdata.stock));
    } else if (
      formdata.variation === "Variable" &&
      formdata.combinedValue.length > 0
    ) {
      const formattedAttributes = formdata.combinedValue.map((combo) => {
        const values = combo.split(" - ");
        const attributeObj = {};
        formdata.attributes.forEach((attr, index) => {
          attributeObj[attr.toLowerCase()] = values[index];
        });
        return {
          ...attributeObj,
          other_attributes: {
            price: Number(formdata.priceData[combo] || 0),
            discount_price: Number(formdata.discountPriceData[combo] || 0),
            stock: Number(formdata.stockData[combo] || 0),
          },
        };
      });
      // Use price of the first variation as the main price
      submitFormData.append(
        "reguler_price",
        Number(formdata.priceData[formdata.combinedValue[0]] || 0)
      );
      submitFormData.append(
        "discount_price",
        Number(formdata.discountPriceData[formdata.combinedValue[0]] || 0)
      );
      submitFormData.append("attributes", JSON.stringify(formattedAttributes));
    }

    // Append ONLY new images that have been selected by the user
    if (newProductImage) {
      submitFormData.append("productImage", newProductImage);
    }
    newGalleryImages.forEach((file) => {
      submitFormData.append("galleryImages", file);
    });

    try {
      await axios.put(`${baseUrl}api/updateproduct/${id}`, submitFormData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success("Product updated successfully!");
      navigate("/productlist");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update product.");
    } finally {
      setLoading(false);
    }
  };

  // The return statement with JSX for the form
  return (
    <>
      <Topbar />
      <Sidebar />
      <main id="main" className="main">
        <div className="pagetitle">
          <h1>Update Product</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/">Home</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/productlist">Product List</Link>
              </li>
              <li className="breadcrumb-item active">Update Product</li>
            </ol>
          </nav>
        </div>
        <div className="card">
          <div className="card-body">
            <h5 className="card-title">Edit Product Details</h5>
            <form className="row g-3 pt-4" onSubmit={handleSubmit}>
              {/* Product Name */}
              <div className="col-md-4">
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control"
                    id="name"
                    placeholder="Enter Product Name"
                    value={formdata.product_name}
                    onChange={(e) =>
                      setFormdata({ ...formdata, product_name: e.target.value })
                    }
                  />
                  <label htmlFor="name">Product Name</label>
                </div>
              </div>

              {/* Category */}
              <div className="col-md-4">
                <div className="form-floating mb-3">
                  <select
                    className="form-select"
                    id="category"
                    value={formdata.category}
                    onChange={handleChangeCategory}
                  >
                    <option value="">Select Category</option>
                    {categoryList.map((item) => (
                      <option key={item._id} value={item._id}>
                        {item.catagoryname}
                      </option>
                    ))}
                  </select>
                  <label htmlFor="category">Category</label>
                </div>
              </div>
              {/* Sub Category */}
              <div className="col-md-4">
                <div className="form-floating mb-3">
                  <select
                    className="form-select"
                    id="sub_category"
                    value={formdata.sub_category}
                    onChange={(e) =>
                      setFormdata({ ...formdata, sub_category: e.target.value })
                    }
                  >
                    <option value="">Select Sub Category</option>
                    {subCategoryList.map((item) => (
                      <option key={item._id} value={item._id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                  <label htmlFor="sub_category">Sub Category</label>
                </div>
              </div>
              {/* SKU */}
              <div className="col-md-4">
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control"
                    id="sku"
                    placeholder="Enter SKU"
                    value={formdata.sku}
                    onChange={(e) =>
                      setFormdata({ ...formdata, sku: e.target.value })
                    }
                  />
                  <label htmlFor="sku">SKU</label>
                </div>
              </div>

              {/* Variation */}
              <div className="col-md-3">
                <div className="form-floating mb-3">
                  <select
                    className="form-select"
                    id="variation"
                    value={formdata.variation}
                    onChange={handleVariationChange}
                  >
                    <option value="Simple">Simple</option>
                    <option value="Variable">Variable</option>
                  </select>
                  <label htmlFor="variation">Product Variation</label>
                </div>
              </div>

              {/* Simple Product Prices */}
              {formdata.variation === "Simple" && (
                <>
                  <div className="col-md-3">
                    <div className="form-floating">
                      <input
                        type="number"
                        className="form-control"
                        id="reguler_price"
                        placeholder="Enter Regular Price"
                        value={formdata.reguler_price}
                        onChange={(e) =>
                          setFormdata({
                            ...formdata,
                            reguler_price: e.target.value,
                          })
                        }
                      />
                      <label htmlFor="reguler_price">Regular Price</label>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-floating">
                      <input
                        type="number"
                        className="form-control"
                        id="discount_price"
                        placeholder="Enter Discount Price"
                        value={formdata.discount_price}
                        onChange={(e) =>
                          setFormdata({
                            ...formdata,
                            discount_price: e.target.value,
                          })
                        }
                      />
                      <label htmlFor="discount_price">Discount Price</label>
                    </div>
                  </div>

                  <div className="col-md-3">
                    <div className="form-floating">
                      <input
                        type="number"
                        className="form-control"
                        id="stock"
                        placeholder="Enter Stock"
                        value={formdata.stock}
                        onChange={(e) =>
                          setFormdata({ ...formdata, stock: e.target.value })
                        }
                      />
                      <label htmlFor="stock">Stock</label>
                    </div>
                  </div>
                </>
              )}

              {/* Variable Product Attributes */}
              {formdata.variation === "Variable" && (
                <>
                  <div className="col-md-4">
                    <div className="form-floating">
                      <input
                        type="text"
                        className="form-control"
                        id="attribute_name"
                        placeholder="Enter Attribute Name (e.g., Color)"
                        value={attributeName}
                        onChange={handleAttributeNameChange}
                      />
                      <label htmlFor="attribute_name">Attribute Name</label>
                    </div>
                  </div>

                  <div className="col-md-2 d-flex align-items-center mt-0">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleAddAttribute}
                    >
                      Add Attribute
                    </button>
                  </div>

                  {formdata.attributes.map((attribute, index) => (
                    <React.Fragment key={index}>
                      <div className="col-md-4">
                        <div className="form-floating">
                          <input
                            type="text"
                            className="form-control"
                            id={`attribute_${attribute}`}
                            placeholder={`Enter value for ${attribute}`}
                            value={tempValues[attribute] || ""}
                            onChange={(e) =>
                              handleAttributeValueChange(attribute, e)
                            }
                          />
                          <label htmlFor={`attribute_${attribute}`}>
                            {attribute}
                          </label>
                        </div>
                      </div>
                      <div className="col-md-2 d-flex align-items-center mt-3">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => handleAddValue(attribute)}
                        >
                          Add Value
                        </button>
                      </div>
                    </React.Fragment>
                  ))}

                  {formdata.combinedValue.length > 0 && (
                    <div className="col-12">
                      <h5 className="my-3">Product Variations</h5>
                      {formdata.combinedValue.map((combo, idx) => (
                        <div key={idx} className="row mb-3 align-items-center">
                          <div className="col-md-2">
                            <span className="badge bg-info p-2">{combo}</span>
                          </div>
                          <div className="col-md-3">
                            <div className="form-floating">
                              <input
                                type="number"
                                className="form-control"
                                placeholder="Regular Price"
                                id={`price_${combo}`}
                                value={formdata.priceData[combo] || ""}
                                onChange={(e) => handlePriceChange(combo, e)}
                              />
                              <label htmlFor={`price_${combo}`}>
                                Regular Price
                              </label>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="form-floating">
                              <input
                                type="number"
                                className="form-control"
                                placeholder="Discount Price"
                                id={`discount_${combo}`}
                                value={formdata.discountPriceData[combo] || ""}
                                onChange={(e) =>
                                  handleDiscountPriceChange(combo, e)
                                }
                              />
                              <label htmlFor={`discount_${combo}`}>
                                Discount Price
                              </label>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="form-floating">
                              <input
                                type="number"
                                className="form-control"
                                placeholder="Stock"
                                id={`stock_${combo}`}
                                value={formdata.stockData[combo] || ""}
                                onChange={(e) => handleStockChange(combo, e)}
                              />
                              <label htmlFor={`stock_${combo}`}>Stock</label>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Product & Gallery Images */}
              <div className="col-md-6">
                <label htmlFor="productImage" className="form-label">
                  Product Image
                </label>
                <input
                  type="file"
                  className="form-control"
                  id="productImage"
                  onChange={handleProductImageChange}
                  accept="image/*"
                />
                {imagePreview && (
                  <div className="mt-2">
                    <img
                      src={imagePreview}
                      alt="Product preview"
                      className="img-thumbnail"
                      style={{ maxHeight: "200px" }}
                    />
                  </div>
                )}
              </div>

              <div className="col-md-6">
                <label htmlFor="galleryImages" className="form-label">
                  Gallery Images
                </label>
                <input
                  type="file"
                  className="form-control"
                  id="galleryImages"
                  multiple
                  onChange={handleGalleryImagesChange}
                  accept="image/*"
                />
              </div>

              {galleryPreviews.length > 0 && (
                <div className="col-12">
                  <div className="preview-images d-flex flex-wrap gap-2 mt-2">
                    {galleryPreviews.map((previewUrl, index) => (
                      <img
                        key={index}
                        src={previewUrl}
                        alt={`Gallery preview ${index + 1}`}
                        className="img-thumbnail"
                        style={{
                          height: "100px",
                          width: "100px",
                          objectFit: "cover",
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Description */}
              <div className="col-md-12">
                <label htmlFor="description" className="form-label">
                  Description
                </label>
                <JoditEditor
                  ref={editor}
                  value={formdata.description}
                  onChange={(newContent) =>
                    setFormdata({ ...formdata, description: newContent })
                  }
                />
              </div>

              {/* Product Display Shift (Multi-select) */}
              <div className="col-12 mt-3">
                <div className="p-3 border rounded">
                  <label className="form-label d-block mb-3 fw-bold">Product Display Shift</label>
                  <div className="d-flex flex-wrap gap-3">
                    {shiftList.map((shift) => (
                      <div key={shift._id} className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`shift_${shift._id}`}
                          value={shift._id}
                          checked={formdata.displayShifts.includes(shift._id)}
                          onChange={(e) => {
                            const val = e.target.value;
                            const isChecked = e.target.checked;
                            setFormdata(prev => ({
                              ...prev,
                              displayShifts: isChecked
                                ? [...prev.displayShifts, val]
                                : prev.displayShifts.filter(id => id !== val)
                            }));
                          }}
                        />
                        <label className="form-check-label" htmlFor={`shift_${shift._id}`}>
                          {shift.name} <br />
                          <small className="text-muted">{formatTime(shift.startTime)} - {formatTime(shift.endTime)}</small>
                        </label>
                      </div>
                    ))}
                    {shiftList.length === 0 && <span className="text-muted">No shifts found</span>}
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="text-start mt-4">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? "Updating..." : "Update Product"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </>
  );
}

export default UpdateProduct;
